<template>
  <form
    autocomplete="off"
    @submit.prevent="$emit('submit', formData)"
  >
    <fieldset class="p-8 flex flex-col gap-y-4">
      <div class="bg-primary/10 border border-[#ECAA1D] p-3 rounded-md text-black text-sm mb-4">
        Please ensure all required fields are completed before proceeding. Project information cannot be saved if required fields remain blank.
      </div>

      <h3 class="text-lg font-bold text-secondary border-b border-secondary pb-2 mb-4">
        Project Details
      </h3>

      <!-- Project Name -->
      <TextInput
        v-model="formData.label"
        placeholder="Name"
        label="Project Name"
        class="max-w-xl"
        required
        hint="This is a required field"
      />

      <TextEditorInput
        id="write-up"
        v-model="formData.data.writeUp"
        label="Project Write-up"
        recommended-word-count="150 - 250"
        :word-limit="500"
      />

      <!-- Project Level -->
      <div class="input-field max-w-xl !mb-4">
        <div class="flex items-center justify-between mb-1.5">
          <label class="text-sm !mb-0">
            Project Level
            <span class="text-red-600">*</span>
          </label>

          <button
            type="button"
            class="flex items-center gap-1 text-secondary"
            @click="showProjectLevelModal = true"
          >
            <i class="fa-solid fa-circle-question text-lg " />
            <span class="text-sm border-b border-secondary leading-[1]">Level Descriptions</span>
          </button>
        </div>

        <SelectInput
          v-model="formData.data.level"
          required
          nullable
          hint="This is a required field"
          :options="projectLevelOptions"
        />
      </div>

      <!-- Project Owners/Implementing Entity -->
      <label class="required !-mb-1">Project owner/s / Implementing entity </label>
      <TextInput
        v-model="formData.data.projectDetails.owners"
        placeholder="Name"
        class="max-w-xl !mt-0"
        hint="This is a required field"
      />

      <!--  Stakeholders / Social Partners -->
      <label class="!-mb-1">Stakeholders / Social Partners</label>
      <TextInput
        v-model="formData.data.stakeholders"
        placeholder="Name"
        class="max-w-xl !mt-0"
      />

      <!-- Project Timeframe -->
      <fieldset class="flex items-center gap-3 max-w-sm">
        <legend class="mb-3">
          Project Timeframe
        </legend>

        <div>
          <label
            for="start-date"
            class="mb-1"
          >
            Estimated Start Date
          </label>

          <VueDatePicker
            id="start-date"
            v-model="formData.data.projectDetails.startDate"
            :enable-time-picker="false"
          />
        </div>

        <div>
          <label
            for="end-date"
            class="mb-1"
          >
            Estimated End Date
          </label>

          <VueDatePicker
            id="end-date"
            v-model="formData.data.projectDetails.endDate"
            :enable-time-picker="false"
          />
        </div>
      </fieldset>

      <!-- Project Sector/Industry -->
      <SelectInput
        v-model="formData.data.projectDetails.projectSector"
        label="Project sector / Industry"
        :options="projectSectorOptions"
        class="max-w-xs"
      />

      <!-- Proposed Budget -->
      <MaskedCurrencyInput
        v-model="formData.data.projectDetails.budget"
        label="Proposed Budget"
        class="max-w-xs"
      />
    </fieldset>

    <!-- Project Location -->
    <fieldset class="p-8 flex flex-col gap-y-4 bg-[#ECF2EE]">
      <h3 class="text-lg font-bold text-secondary border-b border-secondary pb-2 mb-4">
        Project Location
      </h3>

      <div class="flex justify-between items-end mb-4">
        <fieldset class="flex gap-2">
          <TextInput
            v-model="formData.data.projectDetails.location.latitude"
            type="number"
            label="Latitude"
            class="max-w-xl !mb-0"
            step=".0000000000000001"
          />

          <TextInput
            v-model="formData.data.projectDetails.location.longitude"
            type="number"
            label="Longitude"
            class="max-w-xl !mb-0"
            step=".000000000000000001"
          />
        </fieldset>

        <AppButton
          v-if="hasValidCoordinates"
          class="btn-danger"
          type="button"
          @click="handleDeleteMapMarker"
        >
          Delete Map Marker
        </AppButton>
      </div>

      <div
        class="h-[600px] relative border-2 border-secondary/30 rounded"
        :class="{ '!cursor-crosshair': selectingLocation }"
      >
        <LMap
          ref="map"
          :zoom="6"
          :center="initialLatitude && initialLongitude ? [initialLatitude, initialLongitude] : [-29.84823755156905, 25]"
          :options="{ attributionControl: false, scrollWheelZoom: false }"
          :use-global-leaflet="false"
          style="z-index: 1;"
          class="form-map"
          @click="selectingLocation = false"
          @mousemove="handleMapMouseMove($event)"
        >
          <LTileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            name="OpenStreetMap"
            :no-wrap="true"
          />

          <LMarker
            v-if="hasValidCoordinates"
            :draggable="true"
            :auto-pan="true"
            :auto-pan-padding="50"
            :auto-pan-speed="100"
            :lat-lng="[formData.data.projectDetails.location.latitude, formData.data.projectDetails.location.longitude]"
            @drag="handleMarkerDragEnd($event, formData)"
          />
        </LMap>

        <AppButton
          v-if="!hasValidCoordinates"
          class="btn-solid-secondary absolute bottom-3 z-[1] left-[41%] !rounded-full"
          type="button"
          @click="handleAddMapMarker"
        >
          <i class="fa-solid fa-location-dot text-lg" />
          Add Map Marker
        </AppButton>
      </div>
    </fieldset>

    <!-- Development Impact -->
    <fieldset class="p-8 flex flex-col gap-y-4">
      <h3 class="text-lg font-bold text-secondary border-b border-secondary pb-2 mb-4">
        Development Impact
      </h3>

      <!-- The Table Container -->
      <div class="border border-gray-300 rounded-md bg-white overflow-hidden">
        <!-- Row 1 -->
        <div class="grid grid-cols-[1fr_180px] border-b border-gray-300">
          <label class="p-3 mb-0 flex items-center">What is the estimated number of households that will benefit from this project?</label>
          <div class="p-3 border-l border-gray-300 bg-white">
            <MaskedNumberInput
              v-model="formData.data.developmentImpact.householdNumber"
              class="!w-full !min-w-0"
            />
          </div>
        </div>

        <!-- Row 2 -->
        <div class="grid grid-cols-[1fr_180px] border-b border-gray-300">
          <label class="p-3 mb-0 flex items-center">What is the estimated number of people in your district/metro who will benefit from this project?</label>
          <div class="p-3 border-l border-gray-300 bg-white">
            <MaskedNumberInput
              v-model="formData.data.developmentImpact.populationNumber"
              class="!w-full !min-w-0"
            />
          </div>
        </div>

        <!-- Row 3 -->
        <div class="grid grid-cols-[1fr_180px] border-b border-gray-300">
          <label class="p-3 mb-0 flex items-center">What is the estimated number of Women, Youth and Persons with Disabilities (WYPD) who will benefit from this project?</label>
          <div class="p-3 border-l border-gray-300 bg-white">
            <MaskedNumberInput
              v-model="formData.data.developmentImpact.wypdNumber"
              class="!w-25 !min-w-0"
            />
          </div>
        </div>

        <!-- Row 4 -->
        <div class="grid grid-cols-[1fr_180px] border-b border-gray-300">
          <label class="p-3 mb-0 flex items-center">What is the estimated number of temporary or construction jobs that will be created by this project?</label>
          <div class="p-3 border-l border-gray-300 bg-white">
            <MaskedNumberInput
              v-model="formData.data.developmentImpact.temporaryJobNumber"
              class="!w-full !min-w-0"
            />
          </div>
        </div>

        <!-- Row 5 -->
        <div class="grid grid-cols-[1fr_180px] border-b border-gray-300">
          <label class="p-3 mb-0 flex items-center">What is the estimated number of permanent jobs that will be created by this project?</label>
          <div class="p-3 border-l border-gray-300 bg-white">
            <MaskedNumberInput
              v-model="formData.data.developmentImpact.permanentJobNumber"
              class="!w-full !min-w-0"
            />
          </div>
        </div>

        <!-- Row 6: Beneficiary Size -->
        <div class="grid grid-cols-[1fr_180px]">
          <div class="p-3 flex flex-col justify-center">
            <label class="mb-0">Beneficiary size (for Level 3 project only)</label>
            <small class="text-xs">
              *Number of households that will benefit from the project
            </small>
          </div>

          <div class="p-3 border-l border-gray-300 bg-white">
            <MaskedNumberInput
              v-model="formData.data.developmentImpact.beneficiarySize"
              :disabled="formData.data.level !== projectLevels[2].level"
              class="!w-full !min-w-0"
            />
          </div>
        </div>
      </div>
    </fieldset>

    <!-- Strategic Alignments -->
    <fieldset class="p-8 flex flex-col gap-y-4 bg-[#ECF2EE]">
      <h3 class="text-lg font-bold text-secondary border-b border-secondary pb-2 mb-4">
        Strategic Alignments
      </h3>

      <FrameworkCategoriesSelect
        v-model="formData.data.alignments"
        :frameworks="frameworks"
        @show-framework-overview="frameworkForOverview = $event"
      />
    </fieldset>

    <!-- Submit buttons -->
    <div class="px-6 py-3 rounded-b-md flex items-center gap-2 border-t">
      <AppButton
        v-if="formData.id"
        type="button"
        class="btn-danger"
        @click="showDeleteModal = true"
      >
        Delete Project
      </AppButton>

      <div class="flex items-center gap-2 ml-auto">
        <AppButton
          class="btn-outline"
          type="button"
          @click="$emit('cancel')"
        >
          Cancel
        </AppButton>

        <AppButton
          class="btn btn-solid-green"
          :disabled="!canSave"
        >
          Save
        </AppButton>
      </div>
    </div>
  </form>

  <FrameworkOverviewModal
    v-if="frameworkForOverview"
    :framework="frameworkForOverview"
    @close="frameworkForOverview = null"
  />

  <ProjectLevelModal
    v-if="showProjectLevelModal"
    @close="showProjectLevelModal = false"
  />

  <ConfirmationModal
    v-if="showDeleteModal"
    :node-name="node.label"
    :type="node.type"
    :processing="processing"
    intent="danger"
    @delete="$emit('delete', formData.id)"
    @close="showDeleteModal = false"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { LMap, LMarker, LTileLayer } from '@vue-leaflet/vue-leaflet';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import { AppButton } from '@/components/buttons';
import { SelectInput, FrameworkCategoriesSelect, TextInput, TextEditorInput, MaskedCurrencyInput, MaskedNumberInput } from '@/components/form-inputs';
import { FrameworkOverviewModal } from '@/components/framework';
import { ProjectLevelModal, ConfirmationModal } from '@/components/modals';

import { projectLevels } from '@/const';

defineEmits(['cancel', 'submit', 'delete']);

const props = defineProps({
  node: {
    type: Object,
    default: () => ({}),
  },
  frameworks: {
    type: Array,
    default: () => [],
  },
});

const projectLevelOptions = projectLevels.map(level => ({
  label: level.fullName,
  value: level.level,
}));

const formData = ref(JSON.parse(JSON.stringify(props.node)));
const frameworkForOverview = ref();
const showProjectLevelModal = ref(false);
const showDeleteModal = ref(false);
const processing = ref(false);
const selectingLocation = ref(false);
const map = ref();
const initialLatitude = ref(props.node.data.projectDetails.location.latitude);
const initialLongitude = ref(props.node.data.projectDetails.location.longitude);

// The save button should be disabled if the Project name is null
// NOTE: The other fields must indicate that they are required but the project should be able to be saved. Only name is required.
const canSave = computed(() => {
  // !! converts the result to a strict boolean
  return !!(formData.value.label);
});

const hasValidCoordinates = computed(() => {
  const { latitude, longitude } = formData.value.data.projectDetails.location;

  const latitudeValid = typeof latitude === 'number' && latitude >= -90 && latitude <= 90;
  const longitudeValid = typeof longitude === 'number' && longitude >= -180 && longitude <= 180;

  return latitudeValid && longitudeValid;
});

const projectSectorOptions = [
  { label: 'Services', value: 'Services' },
  { label: 'Economy', value: 'Economy' },
  { label: 'Agriculture', value: 'Agriculture' },
  { label: 'Education', value: 'Education' },
  { label: 'Infrastructure', value: 'Infrastructure' },
  { label: 'Social sector', value: 'Social sector' },
];

const handleMarkerDragEnd = (event, formData) => {
  const { lat, lng } = event.target.getLatLng();

  // Update the project's data
  formData.data.projectDetails.location.latitude = lat;
  formData.data.projectDetails.location.longitude = lng;
};

// Even though the latitude and longitude coordinates are hardcoded, their values will be overwritten as soon as the user moves their mouse
const handleAddMapMarker = () => {
  selectingLocation.value = true;

  formData.value.data.projectDetails.location.latitude = -27;
  formData.value.data.projectDetails.location.longitude = 30;
};

// The `wrap()` method constrains the marker coordinates to the viewable map tile
const handleMapMouseMove = (event) => {
  if (!selectingLocation.value) {
    return;
  }

  const { lat, lng } = event.latlng.wrap();

  formData.value.data.projectDetails.location.latitude = lat;
  formData.value.data.projectDetails.location.longitude = lng;
};

const handleDeleteMapMarker = () => {
  formData.value.data.projectDetails.location.latitude = null;
  formData.value.data.projectDetails.location.longitude = null;
  selectingLocation.value = false;
};

watch(
  () => formData.value.data.level,
  (newVal) => {
    if (newVal !== 'Level 3: Major Needs Projects') {
      formData.value.data.developmentImpact.beneficiarySize = 0;
    }
  },
);
</script>

<style>
.form-map {
  @apply outline-none;
  box-shadow: 0 4px 4px #0F513230;

  > * {
    @apply rounded-t;
  }
}
</style>
